import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getJwtToken } from '../utils/jwtService';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { Eraser, Info, Settings, RotateCcw } from 'lucide-react';
import './HostControl.css';

const HostControl = () => {
  const { meetingId, authenticated } = useAuth();
  const [hostInstructions, setHostInstructions] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [sessionMeetingId, setSessionMeetingId] = useState('');
  const [sessionPassword, setSessionPassword] = useState('');

  useEffect(() => {
    // Set initial meeting ID if available from auth context
    if (meetingId) {
      setSessionMeetingId(meetingId);
    }
  }, [meetingId]);

  const handleResetHistory = async () => {
    if (!sessionMeetingId) {
      setError('Meeting ID is required');
      return;
    }

    if (!authenticated) {
      setError('Not authenticated. Please log in first.');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      // Check JWT token
      if (!getJwtToken()) {
        setError('Not authenticated. Please log in first.');
        setIsLoading(false);
        return;
      }

      // Use the functions URL from configuration or default
      const baseUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api';

      const requestBody = {
        meetingPassword: sessionPassword || undefined,
        hostInstructions: hostInstructions.trim() || undefined
      };

      const response = await fetchWithAuth(
        `${baseUrl}/sessions/${encodeURIComponent(sessionMeetingId)}/reset-history`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset translator history');
      }

      setMessage('Reset request submitted successfully');
      setHostInstructions(''); // Clear the instructions after successful reset

      // Clear message after 5 seconds
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error('Error resetting translator history:', err);
      setError(err.message || 'Failed to reset translator history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearForm = () => {
    setHostInstructions('');
    setMessage('');
    setError('');
  };

  return (
    <div className="host-control-container">
      <div className="host-control-card">
        <h2><Settings className="icon-inline" size={24} /> Host Control Panel</h2>
        <p className="description">
          Manage your translation session settings. You can reset the translator's conversation history
          and update instructions for better context during long sessions.
        </p>

        <div className="form-section">
          <h3>Session Information</h3>
          <div className="form-group">
            <label htmlFor="meetingId">Meeting ID</label>
            <input
              type="text"
              id="meetingId"
              value={sessionMeetingId}
              onChange={(e) => setSessionMeetingId(e.target.value)}
              placeholder="Enter meeting ID"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Meeting Password (Optional)</label>
            <input
              type="password"
              id="password"
              value={sessionPassword}
              onChange={(e) => setSessionPassword(e.target.value)}
              placeholder="Enter meeting password (if required)"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Reset Translator History</h3>
          <p className="section-description">
            Use this when starting a new segment in your meeting (e.g., new speaker, new topic).
            This clears the translator's conversation history for a fresh start.
          </p>

          <div className="form-group">
            <label htmlFor="hostInstructions">
              New Host Instructions (Optional)
              <span className="label-hint">Provide context for the translator about the upcoming segment</span>
            </label>
            <textarea
              id="hostInstructions"
              value={hostInstructions}
              onChange={(e) => setHostInstructions(e.target.value)}
              placeholder="e.g., 'The next speaker will discuss technical implementation details about cloud architecture'"
              rows="4"
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {message && (
            <div className="success-message">
              {message}
            </div>
          )}

          <div className="button-group">
            <button
              onClick={handleResetHistory}
              disabled={isLoading || !sessionMeetingId}
              className="reset-button"
            >
              <RotateCcw size={18} /> {isLoading ? 'Resetting...' : 'Reset Translator History'}
            </button>
            <button
              onClick={handleClearForm}
              disabled={isLoading}
              className="clear-button"
            >
              <Eraser size={18} /> Clear Form
            </button>
          </div>
        </div>

        <div className="info-section">
          <h4><Info size={18} className="icon-inline" /> When to use this feature:</h4>
          <ul>
            <li>Starting a new presentation or talk within the same meeting</li>
            <li>Switching to a different topic that requires different context</li>
            <li>After a long break when the previous context is no longer relevant</li>
            <li>When you want to provide specific instructions for the upcoming segment</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default HostControl;